import React from 'react';

import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { HelpService } from '../../commonInfra/services/HelpService';
import { cssClass } from '../../style';
import { FlowRunErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { FlowRunError } from '../../ts/ServerApi/types/FlowRunError';

const helpUrl = cssClass('flowMessage', 'helpUrl', {
  paddingLeft: CommonStyles.Sizes.SmallSpace
});

// Map of error codes with help content, and the tokens for them
export const errorCodesWithHelpContent: {[errorCode: number]: string} = {
  [FlowRunErrorCode.FlowRunUnapprovedRead]: 'prep-conductor-networkshare-safelist',
  [FlowRunErrorCode.FlowRunUnapprovedWrite]: 'prep-conductor-networkshare-safelist',
};

// Some errors can have in-built links, use this function to have them built in as a react component
export function flowRunErrorMessageWithLinks(error: FlowRunError) {
  switch(error.flowRunErrorCode) {
    case FlowRunErrorCode.FlowRunUnapprovedRead:
      return [Messages.errorFlowRunUnapprovedRead(),
        <a key={FlowRunErrorCode.FlowRunUnapprovedRead} className={helpUrl} href={buildHelpUrl(errorCodesWithHelpContent[error.flowRunErrorCode])}>{Messages.learnMore()}</a>];
    case FlowRunErrorCode.FlowRunUnapprovedWrite:
      return [Messages.errorFlowRunUnapprovedWrite(),
        <a key={FlowRunErrorCode.FlowRunUnapprovedWrite} className={helpUrl} href={buildHelpUrl(errorCodesWithHelpContent[error.flowRunErrorCode])}>{Messages.learnMore()}</a>];
    default:
      return flowRunErrorMessageStrings(error);
  }
}

export function flowRunErrorMessageStrings(error: FlowRunError) {
  switch (error.flowRunErrorCode) {
    case FlowRunErrorCode.FlowRunUnknownError:
      return Messages.errorFlowRunUnknown();
    case FlowRunErrorCode.FlowRunLoomSystemError:
      // This error is intentionally listed as unknown: there isn't sufficient information for a more detailed message.
      return Messages.errorFlowRunUnknown();
    case FlowRunErrorCode.FlowRunTransientExecutionError:
    case FlowRunErrorCode.FlowRunExecutionError:
      return Messages.errorFlowRunExecution();
    case FlowRunErrorCode.FlowRunNodeExecutionError:
      return Messages.errorFlowRunNodeExecution({ STEP_NAME: L10n.elideIfLong(error.errorObjectName)});
    case FlowRunErrorCode.FlowRunPythonScriptExecutionError:
      return Messages.errorFlowRunNodeExecution({ STEP_NAME: L10n.elideIfLong(error.errorObjectName)});
    case FlowRunErrorCode.FlowRunMaestroDocumentError:
      // This error is intentionally listed as unknown: there isn't sufficient information for a more detailed message.
      return Messages.errorFlowRunUnknown();
    case FlowRunErrorCode.FlowRunUnsupportedDocumentFeature:
      return Messages.errorFlowRunUnsupportedDocumentFeature();
    case FlowRunErrorCode.FlowRunInputConnectionError:
      return Messages.errorFlowRunInputConnectivity({ CONNECTION_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunOutputError:
      return Messages.errorFlowRunOutputConnectivity({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunPublishingError:
      return Messages.errorFlowRunPublishingOutput({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunInvalidUsernameOrPasswordError:
      return Messages.errorFlowRunInvalidUsernameOrPassword();
    case FlowRunErrorCode.FlowRunInputDriverError:
      return Messages.errorFlowRunInputDriver();
    case FlowRunErrorCode.FlowRunUnapprovedRead:
      return Messages.errorFlowRunUnapprovedRead();
    case FlowRunErrorCode.FlowRunUnapprovedWrite:
      return Messages.errorFlowRunUnapprovedWrite();
    case FlowRunErrorCode.FlowRunLoomCompilationError:
      return Messages.errorFlowRunLoomCompilation();
    case FlowRunErrorCode.FlowRunIrrelevantLoomCompiliationError:
      return Messages.errorFlowRunLoomCompilation();
    case FlowRunErrorCode.FlowRunNodeCompilationError:
      return Messages.errorFlowRunNodeCompilation({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunNodeExpressionError:
      return Messages.errorFlowRunNodeExpression({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunTimeoutError:
      return Messages.errorFlowRunTimeout();
    case FlowRunErrorCode.FlowRunPublishDatasourcePermissionsError:
      return Messages.errorFlowRunPermission();
    case FlowRunErrorCode.FlowRunWriteToDBDisabledError:
      return Messages.errorFlowRunWriteToDBDisabled({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunWriteToDBWindowsAuthError:
      return Messages.errorFlowRunWriteToDBWindowsAuth({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunWriteToTdeIsDeprecating:
      return Messages.errorFlowRunWriteToTdeIsDeprecating();
    case FlowRunErrorCode.FlowRunFailedToConnectExternalResourceError:
      return Messages.errorFlowRunFailedToConnectExternalResourceError({ CONNECTION_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunMissingConnectionInformation:
      return Messages.errorFlowRunMissingConnectionInformation({ CONNECTION_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunCannotConnectInputDatasource:
      return Messages.errorFlowRunCannotConnectInputDatasource({ CONNECTION_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunInputDatasourceMissingDBName:
      return Messages.errorFlowRunInputDatasourceMissingDBName({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunInputDatasourceHasUserReference:
      return Messages.errorFlowRunInputDatasourceHasUserReference();
    case FlowRunErrorCode.FlowRunPublishOutputMissingURL:
      return Messages.errorFlowRunPublishOutputMissingURL({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunPublishProjectNotFound:
      return Messages.errorFlowRunPublishProjectNotFound();
    case FlowRunErrorCode.FlowRunPublishFailedPermissionDenied:
      return Messages.errorFlowRunPublishFailedPermissionDenied();
    case FlowRunErrorCode.FlowRunPublishDatasourceFailed:
      return Messages.errorFlowRunPublishDatasourceFailed({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunPublishDataRoleFailed:
      return Messages.errorFlowRunPublishDataRoleFailed({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunGeneralPublishingError:
      return Messages.errorFlowRunGeneralPublishingError();
    case FlowRunErrorCode.FlowRunOutputsLocationConflict:
      return Messages.errorFlowRunOutputsLocationConflict({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunOutputLocationConflictWithInput:
      return Messages.errorFlowRunOutputLocationConflictWithInput({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunCancelledDueToLimits:
      return Messages.errorFlowRunCancelledDueToLimits();
    case FlowRunErrorCode.FlowRunCannotWriteToDatabase:
      return Messages.errorFlowRunCannotWriteToDatabase();
    case FlowRunErrorCode.FlowRunMissingOutputStepWithSchedule:
      return Messages.errorFlowRunMissingOutputStepWithSchedule();
    case FlowRunErrorCode.FlowRunMissingOutputStep:
      return Messages.errorFlowRunMissingOutputStep();
    case FlowRunErrorCode.FlowRunMissingTableNameForWriteToDatabase:
      return Messages.errorFlowRunMissingTableNameForWriteToDatabase();
    case FlowRunErrorCode.FlowRunVizportalAuthenticationError:
      return Messages.errorFlowRunVizportalAuthenticationError();
    case FlowRunErrorCode.FlowRunIncrementalRefreshOutputMustRunTogether:
      return Messages.errorFlowRunIncrementalRefreshOutputMustRunTogether({ OUTPUT_STEPS: L10n.elideIfLong(error.errorMessageParams[0]) });
    case FlowRunErrorCode.FlowRunIncrementalRefreshOutputMustRunInSameMode:
      return Messages.errorFlowRunIncrementalRefreshOutputMustRunInSameMode({ OUTPUT_STEPS: L10n.elideIfLong(error.errorMessageParams[0]) });
    case FlowRunErrorCode.FlowRunIncrementalRefreshFilterIsNull:
      return Messages.errorFlowRunIncrementalRefreshFilterIsNull({ STEP_NAME: L10n.elideIfLong(error.errorMessageParams[0]) });
    case FlowRunErrorCode.FlowRunIncrementalRefreshFailed:
      return Messages.errorFlowRunIncrementalRefreshFailed({ STEP_NAME: L10n.elideIfLong(error.errorMessageParams[0]) });
    case FlowRunErrorCode.FlowRunWriteOptionNotSpecified:
      return Messages.errorFlowRunWriteOptionNotSpecified();
    default:
      return Messages.errorFlowRunUnknown();
  }
}

// flow document related errors need to be fix in Prep Builder
const flowDocumentRelatedError: {[index: number]: boolean} = {
  [FlowRunErrorCode.FlowRunMaestroDocumentError] : true,
  [FlowRunErrorCode.FlowRunUnsupportedDocumentFeature] : true,
  [FlowRunErrorCode.FlowRunPythonScriptExecutionError] : true,
  [FlowRunErrorCode.FlowRunMissingConnectionInformation] : true,
  [FlowRunErrorCode.FlowRunCannotConnectInputDatasource] : true,
  [FlowRunErrorCode.FlowRunInputDatasourceMissingDBName] : true,
  [FlowRunErrorCode.FlowRunPublishOutputMissingURL] : true,
  [FlowRunErrorCode.FlowRunPublishProjectNotFound] : true,
  [FlowRunErrorCode.FlowRunLoomCompilationError] : true,
  [FlowRunErrorCode.FlowRunIrrelevantLoomCompiliationError] : true,
  [FlowRunErrorCode.FlowRunNodeCompilationError] : true,
  [FlowRunErrorCode.FlowRunNodeExpressionError] : true,
  [FlowRunErrorCode.FlowRunOutputsLocationConflict] : true,
  [FlowRunErrorCode.FlowRunOutputLocationConflictWithInput] : true,
  [FlowRunErrorCode.FlowRunWriteToDBWindowsAuthError] : true,
  [FlowRunErrorCode.FlowRunWriteToTdeIsDeprecating]: true,
  [FlowRunErrorCode.FlowRunCannotWriteToDatabase] : true,
  [FlowRunErrorCode.FlowRunMissingTableNameForWriteToDatabase] : true,
  [FlowRunErrorCode.FlowRunIncrementalRefreshFilterIsNull] : true,
};

// all connection related errors which is should be displayed in connection sub-place
const connectionRelatedError: {[index: number]: boolean} = {
  [FlowRunErrorCode.FlowRunInputConnectionError] : true,
  [FlowRunErrorCode.FlowRunInvalidUsernameOrPasswordError] : true,
  [FlowRunErrorCode.FlowRunInputDriverError] : true,
  [FlowRunErrorCode.FlowRunUnapprovedRead] : true,
  [FlowRunErrorCode.FlowRunFailedToConnectExternalResourceError] : true,
  [FlowRunErrorCode.FlowRunMissingConnectionInformation] : true,
  [FlowRunErrorCode.FlowRunCannotConnectInputDatasource] : true,
  [FlowRunErrorCode.FlowRunInputDatasourceHasUserReference] : true,
  [FlowRunErrorCode.FlowRunWriteToDBWindowsAuthError] : true,
};

// Find if there is an error that needs to be fixed in Prep Desktop
export function needsFixInPrepDesktop(errors: ReadonlyArray<FlowRunError>): boolean {
  return errors.find((error) => {
    return flowDocumentRelatedError[error.flowRunErrorCode];
  }) !== undefined;
}

// Find if there is an error that needs to be fixed in Prep Desktop
export function isConnectionRelatedError(error: FlowRunError): boolean {
  return connectionRelatedError[error.flowRunErrorCode] !== undefined;
}

export function buildHelpUrl(helpRedirectToken: string): string {
  return HelpService.getServerHelpUrlByCategory(helpRedirectToken);
}
